import { Box, CircularProgress, Grid } from "@mui/joy";
import DarkCard from "../../../common/DarkCard";
import DarkTypography from "../../../common/DarkTypography";
import DarkInput from "../../../common/DarkInput";
import IconButtonCTA from "../../../common/IconButtonCTA";
import {
  SSave,
  OpenInNewIcon,
  AddIcon,
  CloudUploadIcon,
  SDelete,
} from "../../../../styles/StyledMUI";
import type { TrainingCourse } from "../../../../api/training/Training.typed";

type Props = {
  actionsWidth: number;
  isCoursesLoading: boolean;
  courses: TrainingCourse[] | null;
  canEdit: boolean;
  editingCourseId: string | null;
  savingCourseId: string | null;
  deletingCourseId: string | null;
  replacingCourseId: string | null;
  addingCourse: boolean;
  newCourseName: string;
  newCourseFile: File | null;
  onCourseNameDraft: (courseId: string, name: string) => void;
  onEditingCourse: (courseId: string | null) => void;
  onSaveCourseName: (course: TrainingCourse) => void;
  onOpenCourseUrl: (url: string) => void;
  onReplaceCourseFile: (course: TrainingCourse, file: File | null | undefined) => void;
  onDeleteCourse: (course: TrainingCourse) => void;
  onNewCourseNameChange: (value: string) => void;
  onNewCourseFileChange: (file: File | null) => void;
  onAddCourse: () => void;
};

export default function TrainingTemplateCoursesSection(props: Props) {
  const {
    actionsWidth,
    isCoursesLoading,
    courses,
    canEdit,
    editingCourseId,
    savingCourseId,
    deletingCourseId,
    replacingCourseId,
    addingCourse,
    onCourseNameDraft,
    onEditingCourse,
    onSaveCourseName,
    onOpenCourseUrl,
    onReplaceCourseFile,
    onDeleteCourse,
    newCourseName,
    newCourseFile,
    onNewCourseNameChange,
    onNewCourseFileChange,
    onAddCourse,
  } = props;

  return (
    <Grid xs={12}>
      <DarkCard dense sx={{ mt: 2 }}>
        <DarkTypography colorVariant="accent" sx={{ mb: 1 }}>
          Cours
        </DarkTypography>
        {isCoursesLoading ? (
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <CircularProgress size="sm" />
          </Box>
        ) : (
          (courses ?? []).map((course) => (
            <Grid container key={course.ID} alignItems="center" sx={{ mb: 1 }}>
              <Grid xs sx={{ pr: 2 }}>
                <DarkInput
                  value={course.Name}
                  onChange={(event) => onCourseNameDraft(course.ID, event.target.value)}
                  onFocus={() => onEditingCourse(course.ID)}
                  fullWidth
                  size="md"
                  endDecorator={
                    <Box sx={{ width: 40, display: "flex", justifyContent: "center" }}>
                      {editingCourseId === course.ID ? (
                        <IconButtonCTA
                          ctaVariant="success"
                          aria-label="Sauvegarder le nom du cours"
                          disabled={!canEdit || savingCourseId === course.ID}
                          onClick={() => onSaveCourseName(course)}
                        >
                          {savingCourseId === course.ID ? (
                            <CircularProgress size="sm" />
                          ) : (
                            <SSave />
                          )}
                        </IconButtonCTA>
                      ) : null}
                    </Box>
                  }
                />
              </Grid>
              <Grid sx={{ width: actionsWidth, flex: "0 0 auto" }}>
                <Box
                  sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 1 }}
                >
                  <IconButtonCTA
                    ctaVariant="accent"
                    aria-label="Ouvrir le cours"
                    onClick={() => onOpenCourseUrl(course.Url)}
                  >
                    <OpenInNewIcon />
                  </IconButtonCTA>
                  <IconButtonCTA
                    ctaVariant="accent"
                    aria-label="Remplacer le cours"
                    component="label"
                    disabled={!canEdit || replacingCourseId === course.ID}
                  >
                    {replacingCourseId === course.ID ? (
                      <CircularProgress size="sm" />
                    ) : (
                      <CloudUploadIcon />
                    )}
                    <input
                      hidden
                      type="file"
                      accept="application/pdf"
                      onChange={(event) =>
                        onReplaceCourseFile(course, event.target.files?.[0] ?? null)
                      }
                    />
                  </IconButtonCTA>
                  <IconButtonCTA
                    ctaVariant="danger"
                    aria-label="Supprimer le cours"
                    disabled={!canEdit || deletingCourseId === course.ID}
                    onClick={() => onDeleteCourse(course)}
                  >
                    {deletingCourseId === course.ID ? <CircularProgress size="sm" /> : <SDelete />}
                  </IconButtonCTA>
                </Box>
              </Grid>
            </Grid>
          ))
        )}
        <Grid container alignItems="center" sx={{ mt: 1 }}>
          <Grid xs sx={{ pr: 2 }}>
            <DarkInput
              placeholder="Créer un nouveau cours"
              value={newCourseName}
              onChange={(event) => onNewCourseNameChange(event.target.value)}
              fullWidth
              size="md"
              endDecorator={<Box sx={{ width: 40 }} />}
            />
          </Grid>
          <Grid sx={{ width: actionsWidth, flex: "0 0 auto" }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 1 }}>
              <IconButtonCTA
                ctaVariant="accent"
                aria-label="Importer le fichier du cours"
                component="label"
                sx={{ ml: 0 }}
                disabled={addingCourse}
              >
                <CloudUploadIcon />
                <input
                  hidden
                  type="file"
                  accept="application/pdf"
                  onChange={(event) => onNewCourseFileChange(event.target.files?.[0] ?? null)}
                />
              </IconButtonCTA>
              <IconButtonCTA
                ctaVariant="accent"
                aria-label="Ajouter le cours"
                onClick={onAddCourse}
                disabled={!canEdit || !newCourseFile || addingCourse}
              >
                {addingCourse ? <CircularProgress size="sm" /> : <AddIcon />}
              </IconButtonCTA>
            </Box>
          </Grid>
        </Grid>
      </DarkCard>
    </Grid>
  );
}
